import React from 'react';
import { connect } from 'react-redux';
import SidebarPanel from "./SidebarPanel";
import SidebarMovieInfo from "./SidebarMovieInfo";
import {
  movieTabHideToggle,
  movieTabOpen,
} from '../actions/creators';
import {
  getArea,
  getState,
  isUiMovieTabOpen,
  isUserLogged,
} from '../reducers';
import { areaSelectorOpen, areaSelectorSelectArea, cinemaSelectorOpen } from '../actions/creators/ui';
import { AnalyticsBillboards } from '../utils/analytics';
import BackButton from './common/BackButton';
import { CinemaType, MovieType } from '../types/entities';
import { getSelectedArea } from '../reducers/user/preferences';
import CMX from '../api';
import ShowtimesPanel from './common/Showtimes/ShowtimesPanel';
import ShowtimesAreaSelector from './common/Showtimes/ShowtimesAreaSelector';
import { USER_FAV_CINEMAS_MOVIE_SHOWTIMES_FETCH_SUCCESS } from '../actions/types';

let lastTracked: string | null = null;
let lastMovieTracked: number | null = null;

interface Props {
  area: { type: 'state' | 'area', id: number, name: string } | undefined,
  cinemaId?: number,
  movieId: number,
  movie: MovieType,
  dates: Array<string>,
  date: string,
  filters: {
    format: Array<string>,
    lang: Array<string>,
  },
  filtersSelection: {
    format: Array<string>,
    lang: Array<string>,
  },
  hidden: boolean,
  isUserLogged: boolean,
  loadError: boolean,
  movieTabOpen: boolean,
  showtimesFilterDateChange: (date: string) => void,
  hideToggle: () => void,
  open: () => void,
  clearFilters: () => void,
  openAreaSelector: () => void,
  openCinemaSelector: () => void,
  onAreaSelect: (type: string, id: number, args?: { [key: string]: any }) => void,
}

interface AreaData {
  cinemas: Array<{
    cinema: CinemaType,
    versions: Array<{
      type: Array<string>,
      sessions: Array<{
        datetime: string,
      }>
    }>
  }>
};

interface State {
  areaData: undefined | AreaData,
  areaDataLoading: boolean,
  areaSelectorOpen: boolean,
  dates: undefined | Array<string>,
  favesData: undefined | any,
  favesDataLoading: boolean,
  formats: undefined | Array<string>,
  languages: undefined | Array<string>,
};

class MovieSidebar extends React.Component<Props, State> {
  private ref: React.RefObject<SidebarPanel>;

  constructor(props: Props) {
    super(props);

    this.ref = React.createRef();
    this.onFavCinemaToggle = this.onFavCinemaToggle.bind(this);

    this.state = {
      areaData: undefined,
      areaDataLoading: false,
      areaSelectorOpen: false,
      dates: undefined,
      favesData: undefined,
      favesDataLoading: false,
      formats: undefined,
      languages: undefined,
    };
  }

  componentDidMount() {
    this.props.open();
    lastTracked = null;
    lastMovieTracked = null;

    const { movieId } = this.props;
    if (!movieId) return;
    
    this.trackView();

    if (this.props.area) {
      this.fetchShowtimes();
    }

    if (this.props.isUserLogged) {
      this.fetchFavorites();
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const hasMovieChanged = this.props.movieId !== prevProps.movieId;
    const hasAreaChanged = this.props.area && prevProps.area ? this.props.area.id !== prevProps.area.id || this.props.area.type !== prevProps.area.type : this.props.area !== prevProps.area;
    const hasUserChanged = this.props.isUserLogged !== prevProps.isUserLogged;

    let dates: Array<string> = [];
    let formats: Array<string> = [];
    let languages: Array<string> = [];

    if (this.state.areaData) {
      dates = extractDatesFromAreaData(this.state.areaData);
      languages = extractLanguagesFromAreaData(this.state.areaData);
      formats = extractFormatsFromAreaData(this.state.areaData);
    }

    if (this.state.favesData) {
      dates = dates.concat(extractDatesFromAreaData(this.state.favesData));
      languages = languages.concat(extractLanguagesFromAreaData(this.state.favesData));
      formats = formats.concat(extractFormatsFromAreaData(this.state.favesData));
    }

    dates = dates.filter((value, index, array) => value && array.indexOf(value) === index).sort();
    languages = languages.filter((value, index, array) => value && array.indexOf(value) === index).sort();
    formats = formats.filter((value, index, array) => value && array.indexOf(value) === index).sort();

    if (dates.join(',') !== (prevState.dates ? prevState.dates.join(',') : '')) {
      this.setState({ dates });
    }
    if (languages.join(',') !== (prevState.languages ? prevState.languages.join(',') : '')) {
      this.setState({ languages });
    }
    if (formats.join(',') !== (prevState.formats ? prevState.formats.join(',') : '')) {
      this.setState({ formats });
    }
    
    if (!hasMovieChanged && !hasAreaChanged && !hasUserChanged) {
      return;
    }

    this.props.open();

    this.setState({ dates: undefined });

    if (this.props.area && (hasMovieChanged || hasAreaChanged)) {
      this.fetchShowtimes();
    }

    if (this.props.isUserLogged && (hasMovieChanged || hasUserChanged)) {
      this.fetchFavorites();
    }
    
    if (this.ref && this.ref.current && this.ref.current.scrollTop) {
      this.ref.current.scrollTop();
    }
    
    this.trackView();
  }

  trackView() {
    const movieId = this.props.movie ? this.props.movie.id : null;

    if (!movieId) return;

    if (movieId !== lastMovieTracked) {
      lastMovieTracked = movieId;
      AnalyticsBillboards.trackMovieView(this.props.movie);
    }
  }

  fetchShowtimes(): void {
    if (!this.props.movieId || !this.props.area) {
      return;
    }

    this.setState({ areaDataLoading: true });
    CMX.get(`/movies/${this.props.movieId}/sessions/${this.props.area.type}/${this.props.area.id}`)
      .then(res => {
        this.setState({ areaData: res as State['areaData'] });
      })
      .catch(() => {})
      .finally(() => {
        this.setState({ areaDataLoading: false });
      });
  }

  fetchFavorites(): void {
    this.setState({ favesDataLoading: true });
    CMX.get(`/me/cinemas/movies/${this.props.movieId}/sessions`)
      .then(res => {
        this.setState({ favesData: res });
      })
      .catch(() => {})
      .finally(() => {
        this.setState({ favesDataLoading: false });
      });
  }

  onFavCinemaToggle(cinemaId: number, isFave: boolean) {
    if (!this.state.favesData || !this.state.favesData.cinemas) {
      return;
    }

    if (isFave) {
      this.addCinemaToFaves(cinemaId);
    } else {
      this.removeCinemaFromFaves(cinemaId);
    }
  }

  addCinemaToFaves(cinemaId: number) {
    const cinemaAlreadyInFaves = !!this.state.favesData.cinemas.find(cinema => cinema.cinema.id === cinemaId);
    if (cinemaAlreadyInFaves) {
        return;
    }

    const cinemaToAdd = this.state.areaData && this.state.areaData.cinemas && this.state.areaData.cinemas.find(cinema => cinema.cinema.id === cinemaId);
    if (!cinemaToAdd) {
        return;
    }

    const updated = {...this.state.favesData};
    updated.cinemas.push(cinemaToAdd);
    this.setState({ favesData: updated });
  }

  removeCinemaFromFaves(cinemaId: number) {
    const updated = {...this.state.favesData};
    updated.cinemas = updated.cinemas.filter(cinema => cinema.cinema.id !== cinemaId);
    this.setState({ favesData: updated });
  }

  render() {
    if (!this.props.movieId) {
      return '';
    }

    return (
      <>
        <SidebarPanel ref={this.ref} isHidden={this.props.hidden}>
          <div className='container pad-y'>
            <BackButton label="Cartelera"
              onBackClick={this.props.hideToggle}
              onLabelClick={this.props.hideToggle}
            />
          </div>
          {this.props.movie && <SidebarMovieInfo movie={this.props.movie} open={this.props.movieTabOpen} />}
          <ShowtimesPanel
            area={this.props.area}
            dates={this.state.dates}
            data={{
              faves: {
                data: this.state.favesData,
                loading: this.state.favesDataLoading,
              },
              area: {
                data: this.state.areaData,
                loading: this.state.areaDataLoading,
              }
            }}
            movie={this.props.movie}
            formats={this.state.formats}
            languages={this.state.languages}
            onAreaSelectorOpen={() => this.setState({ areaSelectorOpen: true })}
            onFavCinemaToggle={(cinemaId: number, isFave: boolean) => this.onFavCinemaToggle(cinemaId, isFave)}
          />
          {this.state.areaSelectorOpen && <ShowtimesAreaSelector
            cinemaSelect={false}
            title='Seleccionar zona'
            onClose={() => this.setState({ areaSelectorOpen: false })}
            onAreaSelect={(type, id) => {
              this.setState({ areaSelectorOpen: false });
              this.props.onAreaSelect(type, id, { context: 'global' });
            }}
            onSave={() => {}}
          />}
        </SidebarPanel>
        
      </>
    );
  }
}

function extractDatesFromAreaData(areaData: State['areaData']): Array<string> {
    const dates: Array<string> = [];
    const today = new Date();

    if (!areaData || !areaData.cinemas) {
      return [];
    }
  
    areaData.cinemas.forEach(cinema => {
      cinema.versions.forEach(version => {
        version.sessions.forEach(session => {
          const d = new Date(session.datetime);
          if (d >= today) {
            dates.push(session.datetime.split('T')[0]);
          }
        });
      });
    });

    const uniqueDates = dates.filter((value, index, array) => value && array.indexOf(value) === index).sort();

    return uniqueDates;
}

function extractLanguagesFromAreaData(areaData: State['areaData']): Array<string> {
  let result: Array<string> = [];

  if (!areaData || !areaData.cinemas) {
    return [];
  }

  areaData.cinemas.forEach(cinema => {
    cinema.versions.forEach(version => {
      result = result.concat(version.type.filter(type => 'lang_' === type.slice(0, 5)));
    });
  });

  result = result.filter((value, index, array) => value && array.indexOf(value) === index).sort();

  return result;
}

function extractFormatsFromAreaData(areaData: State['areaData']): Array<string> {
  let result: Array<string> = [];

  if (!areaData || !areaData.cinemas) {
    return [];
  }

  areaData.cinemas.forEach(cinema => {
    cinema.versions.forEach(version => {
      result = result.concat(version.type.filter(type => 'lang_' !== type.slice(0, 5)));
    });
  });

  result = result.filter((value, index, array) => value && array.indexOf(value) === index).sort();

  return result;
}

const mapStateToProps = (state, props) => {
  let selectedArea: { type: 'state' | 'area', id: number, name?: string } | null = getSelectedArea(state);
  let area;

  if (selectedArea && !selectedArea.id) {
    selectedArea = null;
  }
  
  if (selectedArea) {
    if ('state' === selectedArea.type) {
      area = getState(state, selectedArea.id);
    } else {
      area = getArea(state, selectedArea.id);
    }

    if (area) {
      selectedArea.name = area.name;
    }
  }

  return {
    area: selectedArea,
    hidden: state.movieTab.hidden,
    isUserLogged: isUserLogged(state),
    movie: state.entities.movies[props.movieId],
    movieTabOpen: isUiMovieTabOpen(state),
  };
};

const mapDispatchToProps = dispatch => ({
  hideToggle: () => dispatch(movieTabHideToggle()),
  open: () => dispatch(movieTabOpen()),
  openAreaSelector: () => dispatch(areaSelectorOpen()),
  openCinemaSelector: () => dispatch(cinemaSelectorOpen()),
  onAreaSelect: (type: string, id: number, args?: { [key: string]: any }) => dispatch(areaSelectorSelectArea(type, id, args)),
  onFavCinemasMovieShowtimesFetch: (movieId: number, data: any) => dispatch({ type: USER_FAV_CINEMAS_MOVIE_SHOWTIMES_FETCH_SUCCESS, movieId, data }),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(MovieSidebar);